import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/audit/RegisterTable';
import { columnLabel, formatCell, registerError } from '@/lib/auditRegisters';

const HIDDEN = new Set(['id', 'org_id', 'created_by', 'updated_by', 'archived_by', 'dedup_key', 'restricted_fields_hidden', 'source_link', 'cells']);

export function RegisterEntryDetail({ open, onOpenChange, type, register, entry, canManage, onEdit, onChanged }) {
  const [history, setHistory] = useState(null); const [reason, setReason] = useState(''); const [error, setError] = useState(null); const [busy, setBusy] = useState(false);
  const readOnly = register?.read_only;
  useEffect(() => {
    if (!open || !entry || readOnly) { setHistory(null); return; }
    api.get(`/audit/registers/${type}/${entry.id}/history`).then(({ data }) => setHistory(data.items || [])).catch(() => setHistory([]));
    setError(null); setReason('');
  }, [open, entry, type, readOnly]);
  if (!entry) return null;
  const act = async (action) => {
    setBusy(true); setError(null);
    try { const { data } = await api.post(`/audit/registers/${type}/${entry.id}/${action}`, { revision: entry.revision, ...(action === 'archive' && reason ? { reason } : {}) }); onChanged?.(data.data); onOpenChange(false); }
    catch (e) { setError(registerError(e)); } finally { setBusy(false); }
  };
  const fields = Object.keys(entry).filter((k) => !HIDDEN.has(k) && (!register?.read_only || entry[k] !== undefined));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="register-detail-dialog">
        <DialogHeader>
          <DialogTitle>{entry.register_no || entry.incident_no || entry.participant_name || entry.staff_name || entry.role_name || entry.title || 'Entry'}</DialogTitle>
          <DialogDescription>{register?.label}{readOnly ? ' — read-only, derived from the authoritative module' : entry.archived_at ? ' — archived (read-only until restored)' : ''}</DialogDescription>
        </DialogHeader>
        {readOnly && register?.disclosure && <p role="note" className="text-sm rounded-md border p-2 bg-muted/40" data-testid="register-detail-disclosure">{register.disclosure}</p>}
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-sm py-2" data-testid="register-detail-fields">
          {fields.map((k) => (
            <div key={k} className={typeof entry[k] === 'string' && entry[k].length > 80 ? 'sm:col-span-2' : ''}>
              <dt className="text-muted-foreground">{columnLabel(k)}</dt>
              <dd className="font-medium break-words" data-testid={`detail-${k}`}>{['status', 'overall_status', 'authorisation_status'].includes(k) ? <StatusBadge value={entry[k]} overdue={entry.overdue} /> : formatCell(entry[k])}</dd>
            </div>
          ))}
        </dl>
        {readOnly && entry.source_link && <a href={entry.source_link} className="text-sm underline" data-testid="register-detail-source-link">Open authoritative source record</a>}
        {!readOnly && (
          <section aria-labelledby="register-history-title" className="mt-3">
            <h3 id="register-history-title" className="text-sm font-semibold">Audit history</h3>
            {history === null ? <p className="text-sm text-muted-foreground" role="status">Loading history…</p>
              : history.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="register-history-empty">No history recorded.</p>
              : <ol className="text-sm mt-1 space-y-1 list-none p-0" data-testid="register-history">
                {history.slice(0, 10).map((h, i) => <li key={i} className="flex flex-wrap gap-x-2"><time dateTime={h.timestamp}>{formatCell(h.timestamp)}</time><span className="font-medium">{h.action.replace(/_/g, ' ')}</span>{h.actor_name && <span>by {h.actor_name}</span>}{h.to_status && <span>→ {h.to_status}</span>}{h.changed_fields?.length > 0 && <span className="text-muted-foreground">({h.changed_fields.join(', ')})</span>}</li>)}
              </ol>}
          </section>
        )}
        {error && <p role="alert" className="text-sm text-red-700" data-testid="register-detail-error">{error.message} {error.code && <span className="font-mono text-xs">({error.code})</span>}</p>}
        {!readOnly && canManage && (
          <DialogFooter className="gap-2 flex-wrap">
            {!entry.archived_at ? (
              <>
                <div className="flex-1 min-w-[200px]"><Label htmlFor="register-archive-reason" className="sr-only">Archive reason</Label><Input id="register-archive-reason" placeholder="Archive reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="register-archive-reason" /></div>
                <Button type="button" variant="outline" disabled={busy} onClick={() => act('archive')} data-testid="register-archive-btn">Archive</Button>
                <Button type="button" disabled={busy} onClick={() => { onOpenChange(false); onEdit?.(entry); }} data-testid="register-edit-btn">Edit</Button>
              </>
            ) : <Button type="button" disabled={busy} onClick={() => act('restore')} data-testid="register-restore-btn">Restore</Button>}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
